/**
 * ThresholdEventBanner.tsx — WEB.4.UI.14
 *
 * Transient top-center notification fired when an era change crosses a
 * pollution threshold (Medium 20+ / High 35+ / Critical 50+). The store
 * has already applied the resource deltas; this surface just signals to
 * players what happened.
 *
 * Auto-dismisses after 5 seconds. Click to dismiss immediately.
 */

import type React from "react";
import { useEffect } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./ThresholdEventBanner.module.css";

function tierTitle(tier: string): string {
  switch (tier) {
    case "medium":
      return "⚠ POLLUTION WARNING";
    case "high":
      return "⚠⚠ HIGH POLLUTION CRISIS";
    case "critical":
      return "☠ CRITICAL ECOSYSTEM ALERT";
    default:
      return "POLLUTION EVENT";
  }
}

function tierClass(tier: string, styles: Record<string, string>): string {
  if (tier === "critical") return styles.tierCritical ?? "";
  if (tier === "high") return styles.tierHigh ?? "";
  return styles.tierMedium ?? "";
}

export function ThresholdEventBanner(): React.ReactElement | null {
  const lastThresholdEvent = useGameStore((s) => s.lastThresholdEvent);
  const dismissThresholdEvent = useGameStore((s) => s.dismissThresholdEvent);

  useEffect(() => {
    if (lastThresholdEvent === null) return;
    const t = window.setTimeout(dismissThresholdEvent, 5000);
    return () => window.clearTimeout(t);
  }, [lastThresholdEvent, dismissThresholdEvent]);

  if (lastThresholdEvent === null) return null;

  return (
    <button
      type="button"
      className={`${styles.banner} ${tierClass(lastThresholdEvent.tier, styles)}`}
      onClick={dismissThresholdEvent}
      aria-label="Dismiss threshold event notification"
      data-testid="threshold-event-banner"
      data-tier={lastThresholdEvent.tier}
      data-card-id={lastThresholdEvent.cardId}
    >
      <div className={styles.title}>{tierTitle(lastThresholdEvent.tier)}</div>
      <div className={styles.cardName}>{lastThresholdEvent.cardName}</div>
      <div className={styles.body}>{lastThresholdEvent.description}</div>
      <div className={styles.hint}>Click to dismiss</div>
    </button>
  );
}
